/* eslint-disable no-nested-ternary */
import { Autocomplete, Box, MenuItem } from '@mui/material';
import { uniq } from 'lodash';
import { useMemo, useState } from 'react';
import { ArchivedStoreLog, Ingredient } from '../../../apiTypes';
import DataTable, { Column } from '../../../components/DataTable';
import FilterDropdown from '../../../components/FilterDropdown';
import Loader from '../../../components/Loader';
import StyledTextField from '../../../components/styled/StyledTextField';
import { StyledPaper } from '../../../constants/styles';
import { t } from '../../../constants/translations';
import { useAdmin } from '../../../contexts/admin.context';
import { useCafe } from '../../../contexts/cafe.context';
import { useLocale } from '../../../contexts/locale.context';
import getQntLabel from '../../../utils/getQntLabel';
import { Map } from '../../../utils/map';
import { TimeScale } from '../Reports';
import IngredientChart from './IngredientChart';
import { useGetArchivedStoreLogs } from './useGetArchivedStoreLogs';

export type ReportItem = {
  id: string;
  ingredientName: string;
  added: number;
  sold: number;
  production: number;
  moved: number;
  inventored: number;
};

type Row = ReportItem;
type Props = {
  timeRange: [number, number];
  timeScale: TimeScale;
};

export const getReportRowsMap = (logs: ArchivedStoreLog[], ingredientsMap: Map<Ingredient>) => {
  const rowMap: Record<string, ReportItem> = {};
  logs.forEach((log) => {
    Object.entries(log.diff).forEach(([id, qnt]) => {
      const prev: ReportItem = rowMap[id] || {
        id,
        ingredientName: ingredientsMap[id]?.name || id,
        added: 0,
        sold: 0,
        production: 0,
        moved: 0,
        inventored: 0,
      };

      rowMap[id] = {
        id,
        ingredientName: prev.ingredientName,
        added: log.type === 'supply' ? prev.added + qnt : prev.added,
        production: log.type === 'makeSemiProduct' ? prev.production + qnt : prev.production,
        moved: log.type === 'transfer' ? prev.moved + qnt : prev.moved,
        inventored: log.type === 'inventory' ? prev.inventored + qnt : prev.inventored,
        sold:
          log.type === 'addToOrder' ||
          log.type === 'changeOrderItem' ||
          log.type === 'removeFromOrder' ||
          log.type === 'selfOrder'
            ? prev.sold + qnt
            : prev.sold,
      };
    });
  });

  return rowMap;
};

const ReportByStores = ({ timeRange, timeScale }: Props) => {
  const { lang } = useLocale();
  const { cafeSlug } = useAdmin();
  const { ingredientsMap, storesMap, stores, ingredients } = useCafe();
  const [storeId, setStoreId] = useState('');
  const [ingredientId, setIngredientId] = useState('');
  const [workerName, setWorkerName] = useState('');
  const [ingredientType, setIngredientTypee] = useState('');

  const ingredientName = ingredientsMap[ingredientId]?.name || ingredientId;

  const { logs, isLoading } = useGetArchivedStoreLogs(timeRange);

  const ingredientIds = useMemo(
    () =>
      ingredients
        .map((i) => i.id)
        .sort((a, b) => ((ingredientsMap[a]?.category || '') > (ingredientsMap[b]?.category || '') ? 1 : -1)),
    [ingredients, ingredientsMap]
  );
  const storeIds = useMemo(() => stores.map((s) => s.id), [stores]);
  const workerNames = useMemo(() => uniq(logs.map((log) => log.workerName)).filter((o) => !!o), [logs]);
  const filteredLogs = useMemo(() => {
    const filteredByWorker = workerName ? logs.filter((l) => l.workerName === workerName) : logs;
    const filteredByStore = storeId ? filteredByWorker.filter((l) => l.storeId === storeId) : filteredByWorker;
    const filteredByType = ingredientType
      ? filteredByStore
          .map((l) => ({
            ...l,
            diff: Object.fromEntries(
              Object.entries(l.diff).filter(([id]) => ingredientsMap[id]?.type === ingredientType)
            ),
          }))
          .filter((l) => Object.keys(l.diff).length)
      : filteredByStore;
    const filteredByIngredient = ingredientId
      ? filteredByType
          .map((l) => ({
            ...l,
            diff: Object.fromEntries(Object.entries(l.diff).filter(([id]) => id === ingredientId)),
          }))
          .filter((l) => Object.keys(l.diff).length)
      : filteredByType;

    return filteredByIngredient;
  }, [ingredientId, ingredientType, ingredientsMap, logs, storeId, workerName]);

  const rows = useMemo(() => {
    const rowMap = getReportRowsMap(filteredLogs, ingredientsMap);

    return Object.values(rowMap).sort((a, b) => (a.ingredientName > b.ingredientName ? 1 : -1));
  }, [filteredLogs, ingredientsMap]);

  const columns: Column<Row>[] = useMemo(
    () => [
      {
        name: 'name',
        title: t.name[lang],
        align: 'left',
        render: (r: Row) => r.ingredientName,
      },
      {
        name: 'added',
        title: t.supply[lang],
        align: 'right',
        render: (r: Row) => (r.added ? getQntLabel(r.added, ingredientsMap[r.id], lang) : ''),
      },
      {
        name: 'production',
        title: t.semi_finished_products_production[lang],
        align: 'right',
        render: (r: Row) => (r.production ? getQntLabel(r.production, ingredientsMap[r.id], lang) : ''),
      },
      {
        name: 'moved',
        title: t.movement[lang],
        align: 'right',
        render: (r: Row) => (r.moved ? getQntLabel(r.moved, ingredientsMap[r.id], lang) : ''),
      },
      {
        name: 'inventory',
        title: t.inventory[lang],
        align: 'right',
        render: (r: Row) => (r.inventored ? getQntLabel(r.inventored, ingredientsMap[r.id], lang) : ''),
      },
      {
        name: 'sold',
        title: t.menu_item_production[lang],
        align: 'right',
        render: (r: Row) => (r.sold ? getQntLabel(r.sold, ingredientsMap[r.id], lang) : ''),
      },
    ],
    [ingredientsMap, lang]
  );

  if (isLoading) {
    return (
      <Box sx={{ mt: 10 }}>
        <Loader open />
      </Box>
    );
  }

  return (
    <>
      <DataTable
        onRowClick={ingredientId ? undefined : (r) => setIngredientId(r.id)}
        customHeader={
          <>
            <Autocomplete
              options={[...ingredientIds, ingredientName]}
              onChange={(_: unknown, newValue: string | null) => setIngredientId(newValue || '')}
              value={ingredientName}
              size="small"
              PaperComponent={StyledPaper}
              sx={{ width: 202 }}
              disableClearable={!ingredientId}
              renderTags={(v) => ingredientsMap[ingredientId]?.name}
              renderInput={(params) => <StyledTextField label={t.product[lang]} {...params} />}
              renderOption={(props, o) => <MenuItem {...props}>{ingredientsMap[o]?.name || ''}</MenuItem>}
              groupBy={(o) => ingredientsMap[o]?.category || t.unknown[lang]}
              filterOptions={(options, state) =>
                options.filter((o) =>
                  ingredientsMap[o]?.name.toLocaleLowerCase().includes(state.inputValue.toLowerCase())
                )
              }
            />
            <FilterDropdown
              label={t.store[lang]}
              value={storeId}
              setValue={setStoreId}
              options={storeIds}
              renderOption={(o) => storesMap[o]?.name || o}
            />
            <FilterDropdown label={t.worker[lang]} value={workerName} setValue={setWorkerName} options={workerNames} />
            <FilterDropdown
              label={t.type[lang]}
              value={ingredientType}
              setValue={setIngredientTypee}
              options={['product', 'semiProduct']}
              renderOption={(o) => (o === 'product' ? t.product[lang] : t.semifinished_product[lang])}
            />
          </>
        }
        rows={rows}
        columns={columns}
        size="small"
        showActionButtonOnEmpty={false}
      />

      {ingredientId && (
        <IngredientChart ingredientId={ingredientId} timeScale={timeScale} logs={filteredLogs} timeRange={timeRange} />
      )}
    </>
  );
};

export default ReportByStores;
